import { Eye, FileCheck2, FileUp, Loader2, RefreshCw } from "lucide-react";
import type {
  IsoCertificateCode,
  IsoCertificateSlot,
  IsoCertificateStandard,
} from "../types/certificados-iso";
import {
  createEmptyIsoCertificateSlot,
  isoCertificateCodesByStandard,
} from "../types/certificados-iso";
import "../styles/certificados-iso.css";

type Props = {
  standard: IsoCertificateStandard;
  slots: IsoCertificateSlot[];
  loading: boolean;
  canManage?: boolean;
  variant: "card" | "hero";
  onPreview: (slot: IsoCertificateSlot) => void;
  onUpload?: (slot: IsoCertificateSlot) => void;
};

export function IsoCertificateActions({
  standard,
  slots,
  loading,
  canManage = false,
  variant,
  onPreview,
  onUpload,
}: Props) {
  const standardSlots = isoCertificateCodesByStandard[standard].map((codigo) =>
    resolveSlot(slots, codigo, standard),
  );

  if (variant === "card") {
    return (
      <div className="iso-certificates-card-list" aria-label="Certificados oficiales">
        {standardSlots.map((slot) => (
          <button
            key={slot.codigo}
            type="button"
            className="iso-certificates-card-button"
            disabled={loading || !slot.available}
            onClick={() => onPreview(slot)}
          >
            <span className="iso-certificates-card-icon" aria-hidden="true">
              {loading ? <Loader2 className="animate-spin" size={16} /> : <FileCheck2 size={16} />}
            </span>
            <span className="iso-certificates-card-copy">
              <strong>{slot.label}</strong>
              <small>
                {loading
                  ? "Comprobando disponibilidad…"
                  : slot.available
                    ? slot.documento?.nombre_original ?? "Disponible"
                    : "Sin archivo cargado"}
              </small>
            </span>
            {slot.available && !loading ? <Eye size={15} aria-hidden="true" /> : null}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="iso-certificates-hero-actions" aria-label="Certificados oficiales">
      {standardSlots.map((slot) => (
        <div key={slot.codigo} className="iso-certificates-hero-item">
          <span className="iso-certificates-hero-label">{slot.label}</span>
          <div className="iso-certificates-hero-buttons">
            {slot.available ? (
              <button
                type="button"
                className="iso-certificates-hero-button"
                onClick={() => onPreview(slot)}
                disabled={loading}
              >
                {loading ? <Loader2 className="animate-spin" size={15} /> : <Eye size={15} />}
                Ver
              </button>
            ) : (
              <span className="iso-certificates-hero-missing">
                {loading ? "Comprobando…" : "No cargado"}
              </span>
            )}

            {canManage && onUpload ? (
              <button
                type="button"
                className={slot.available
                  ? "iso-certificates-hero-manage iso-certificates-hero-manage-icon"
                  : "iso-certificates-hero-manage"}
                aria-label={slot.available
                  ? `Sustituir ${slot.label}`
                  : `Añadir ${slot.label}`}
                title={slot.available
                  ? `Sustituir ${slot.label}`
                  : `Añadir ${slot.label}`}
                onClick={() => onUpload(slot)}
                disabled={loading}
              >
                {slot.available ? <RefreshCw size={15} /> : <FileUp size={15} />}
                {!slot.available ? "Añadir" : null}
              </button>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}

function resolveSlot(
  slots: IsoCertificateSlot[],
  codigo: IsoCertificateCode,
  standard: IsoCertificateStandard,
): IsoCertificateSlot {
  return slots.find((slot) => slot.codigo === codigo)
    ?? createEmptyIsoCertificateSlot(codigo, standard);
}
